import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import "./Contact.css";

const MAX_FILES = 10;
const MAX_MB_EACH = 12;          // hard reject truly massive files before compress
const MAX_DIM = 2000;            // resize max width/height
const JPEG_QUALITY = 0.82;       // good quality but much smaller

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// Convert any image to a resized JPEG Blob (works for png/jpg/jpeg; HEIC may not preview in-browser)
async function compressImageToJpegBlob(file) {
  const bitmap = await createImageBitmap(file);

  let { width, height } = bitmap;
  const scale = Math.min(1, MAX_DIM / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, width, height);

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Compression failed"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

async function uploadOneToCloudinary(file, attempt = 1) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", UPLOAD_PRESET);

  const resp = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: "POST",
    body: fd,
  });

  if (!resp.ok) {
    const errText = await resp.text();
    if (attempt < 3) {
      await sleep(400 * attempt);
      return uploadOneToCloudinary(file, attempt + 1);
    }
    throw new Error(`Cloudinary upload failed: ${errText}`);
  }

  const data = await resp.json();
  if (!data?.secure_url) throw new Error("Cloudinary returned no secure_url");
  return data.secure_url;
}

async function uploadImagesToCloudinary(files, onProgress) {
  if (!CLOUD_NAME || !UPLOAD_PRESET) {
    throw new Error("Missing Cloudinary env vars.");
  }

  const list = files.slice(0, MAX_FILES);

  // quick pre-check for absurd sizes
  for (const f of list) {
    const mb = f.size / (1024 * 1024);
    if (mb > MAX_MB_EACH) {
      throw new Error(`One of your files is too large (${mb.toFixed(1)}MB). Please choose smaller photos.`);
    }
  }

  const urls = [];

  for (let i = 0; i < list.length; i++) {
    onProgress?.(i, list.length);

    let uploadFile = list[i];

    // Compress most images to make uploads reliable
    try {
      const jpegBlob = await compressImageToJpegBlob(list[i]);
      uploadFile = new File(
        [jpegBlob],
        `${list[i].name.replace(/\.[^.]+$/, "")}.jpg`,
        { type: "image/jpeg" }
      );
    } catch {
      uploadFile = list[i];
    }

    const url = await uploadOneToCloudinary(uploadFile);
    urls.push(url);
  }

  onProgress?.(list.length, list.length);
  return urls;
}

function Contact() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    services: [],
    serviceType: "residential",
    comments: "",
    files: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [fileInputKey, setFileInputKey] = useState(0);

  // ✅ non-blocking “photos failed” message
  const [publicError, setPublicError] = useState("");

  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Email is invalid";

    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^[\d\s-+()]{10,}$/.test(formData.phone)) newErrors.phone = "Phone number is invalid";

    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";

    if (!Array.isArray(formData.services) || formData.services.length === 0) {
      newErrors.services = "Please select at least one service";
    }

    if (formData.files.length > MAX_FILES) newErrors.files = `Max ${MAX_FILES} photos`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files: selectedFiles } = e.target;

    if (type === "checkbox" && name === "services") {
      setFormData((prev) => ({
        ...prev,
        services: checked
          ? [...prev.services, value]
          : prev.services.filter((service) => service !== value),
      }));
      return;
    }

    if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (type === "file") {
      const selected = Array.from(selectedFiles || []).slice(0, MAX_FILES);
      setFormData((prev) => ({ ...prev, files: selected }));
      setErrors((prev) => ({ ...prev, files: undefined }));
      setPublicError(""); // ✅ clear old “upload failed” warning when they change files
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setPublicError(""); // ✅ clear old warning on a new submit

    try {
      const isLocalHost =
        window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      const apiUrl = isLocalHost ? "http://localhost:4000/send-email" : "/api/send-email";

      // 1) Upload photos to Cloudinary (best-effort)
      let imageUrls = [];

      if (formData.files.length > 0) {
        try {
          imageUrls = await uploadImagesToCloudinary(formData.files, (current, total) => {
            setUploadProgress({ current, total });
          });
        } catch (err) {
          console.error("Image upload failed, continuing without photos:", err);
          setPublicError("Some photos couldn't be uploaded. Your request was still sent successfully.");
          imageUrls = []; // ✅ send without photos
          setUploadProgress({ current: 0, total: 0 });
        }
      } else {
        setUploadProgress({ current: 0, total: 0 });
      }

      // 2) Send form + image URLs (tiny payload)
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        services: formData.services,
        serviceType: formData.serviceType,
        comments: formData.comments,
        imageUrls,
      };

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData.error || "Failed to send message");
      }

      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion_event_submit_lead_form");
      }

      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        services: [],
        serviceType: "residential",
        comments: "",
        files: [],
      });
      setUploadProgress({ current: 0, total: 0 });
      setFileInputKey((k) => k + 1);
    } catch (error) {
      console.error(error);
      setSubmitStatus({ type: "error", message: error?.message || "Unknown error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-page">
      <SEO
        title="Exterior Cleaning Quotes Fredericton | Window, Gutter, House & Pressure Washing"
        description="Request a free quote in Fredericton, NB for window cleaning, gutter cleaning, house washing, pressure washing, and polymeric sanding from Prestige Clean."
        keywords="window cleaning quote fredericton, gutter cleaning quote fredericton, house washing quote fredericton, pressure washing quote fredericton, polymeric sanding quote fredericton, exterior cleaning quote fredericton"
        canonicalUrl="https://prestigecleanhomeservices.ca/contact"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Prestige Clean",
            "url": "https://prestigecleanhomeservices.ca/contact"
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://prestigecleanhomeservices.ca/" },
              { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://prestigecleanhomeservices.ca/contact" }
            ]
          }
        ]}
      />

      <div className="contact-hero">
        <span className="contact-hero__eyebrow">Free, no-obligation estimates</span>
        <h1>Request Your Quote</h1>
        <p>
          Tell us what you need, where the property is located, and attach photos if you have them.
          We’ll follow up with pricing and next steps as quickly as possible.
        </p>
      </div>

      <div className="contact-container" id="contact-form">
      <h2>Get a Free Quote</h2>
      <p className="contact-description">
        Fill out the form below to get a free, no-obligation quote. We’ll get back to you ASAP with a plan and pricing.
      </p>

      <form onSubmit={handleSubmit} className="contact-form">
        <div className="form-group">
          <label htmlFor="firstName">First Name *</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={errors.firstName ? "error" : ""}
          />
          {errors.firstName && <span className="error-message">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name *</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={errors.lastName ? "error" : ""}
          />
          {errors.lastName && <span className="error-message">{errors.lastName}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={errors.email ? "error" : ""}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className={errors.phone ? "error" : ""}
          />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="address">Address *</label>
          <input
            type="text"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            className={errors.address ? "error" : ""}
          />
          {errors.address && <span className="error-message">{errors.address}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="city">City *</label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={errors.city ? "error" : ""}
          />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>

        <div className="form-group">
          <label>Service Type *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="serviceType"
                value="residential"
                checked={formData.serviceType === "residential"}
                onChange={handleChange}
              />
              Residential
            </label>

            <label>
              <input
                type="radio"
                name="serviceType"
                value="commercial"
                checked={formData.serviceType === "commercial"}
                onChange={handleChange}
              />
              Commercial
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Services Needed *</label>
          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="services"
                value="window-cleaning"
                checked={formData.services.includes("window-cleaning")}
                onChange={handleChange}
              />
              Window Cleaning
            </label>

            <label>
              <input
                type="checkbox"
                name="services"
                value="gutter-cleaning"
                checked={formData.services.includes("gutter-cleaning")}
                onChange={handleChange}
              />
              Gutter Cleaning
            </label>

            <label>
              <input
                type="checkbox"
                name="services"
                value="polymeric-sanding"
                checked={formData.services.includes("polymeric-sanding")}
                onChange={handleChange}
              />
              Polymeric Sanding
            </label>

            <label>
              <input
                type="checkbox"
                name="services"
                value="house-washing"
                checked={formData.services.includes("house-washing")}
                onChange={handleChange}
              />
              House Washing
            </label>

            <label>
              <input
                type="checkbox"
                name="services"
                value="pressure-washing"
                checked={formData.services.includes("pressure-washing")}
                onChange={handleChange}
              />
              Pressure Washing
            </label>
          </div>

          {errors.services && <span className="error-message">{errors.services}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="comments">Comments</label>
          <textarea
            id="comments"
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            rows="4"
          />
        </div>

        <div className="form-group">
          <label htmlFor="files">Attach Photos (Optional, up to {MAX_FILES})</label>
          <input
            key={fileInputKey}
            type="file"
            id="files"
            name="files"
            multiple
            accept="image/*"
            onChange={handleChange}
          />
          {formData.files.length > 0 && (
            <div style={{ marginTop: "0.5rem" }}>
              {formData.files.length}/{MAX_FILES} selected
            </div>
          )}
          {errors.files && <span className="error-message">{errors.files}</span>}
        </div>

        {isSubmitting && uploadProgress.total > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            Uploading photos: {uploadProgress.current}/{uploadProgress.total}
          </div>
        )}

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? "Sending..." : "Submit"}
        </button>

        {/* ✅ Friendly, non-blocking photo upload warning */}
        {publicError && <div className="error-message">{publicError}</div>}

        {submitStatus === "success" && (
          <div className="success-message">Thank you! We’ll get back to you soon.</div>
        )}

        {submitStatus?.type === "error" && (
          <div className="error-message">
            <strong>Error:</strong> {submitStatus.message}
            <br />
            Please try again or email us at prestigecleanwindows@gmail.com
          </div>
        )}
      </form>
      </div>
    </section>
  );
}

export default Contact;
